import { useGlobalState } from '../../Contexts/globalStateContext';
import { useLeaderboard } from '../../Hooks/useLeaderboard';
import { LeaderboardEntry, LeaderboardScope, ToplistConfig } from '../../Types/leaderboard';
import { Unit } from '../../Types/unit';
import { BizIcon } from '../Mini/BizIcon';
import './biz-toplist.scss';

type BizToplistProps = {
	/** Configuration object for the toplist */
	config: ToplistConfig;
	/** Title to display in the header (optional) */
	title?: string;
	/** Show/hide header (default: true) */
	showHeader?: boolean;
	/** Legacy mode for AngularJS integration */
	legacy?: boolean;
	/** Custom class name */
	className?: string;
};

/**
 * Get a human-readable label for the scope
 */
const getScopeLabel = (scope: LeaderboardScope, unit?: Unit): string => {
	switch (scope) {
		case 'global':
			return 'Global';
		case 'main-unit':
			return unit?.ParentName ?? 'Main Unit';
		case 'unit':
			return unit?.Name ?? 'Unit';
		default:
			return scope;
	}
};

/**
 * Format experience points with thousand separators
 */
const formatXP = (xp: number): string => {
	return xp.toLocaleString();
};

/**
 * BizToplist Component
 *
 * Displays a leaderboard/toplist based on the provided configuration.
 * Fetches data from the LeaderboardsController API endpoints.
 *
 * @example
 * ```tsx
 * <BizToplist
 *   config={{
 *     scope: 'global',
 *     fromDate: new Date('2025-01-01'),
 *     toDate: new Date('2025-12-31'),
 *     limit: 10
 *   }}
 *   title="Top Performers"
 * />
 * ```
 */
const BizToplist = ({ config, title = 'Toplist', showHeader = true, legacy = false, className = '' }: BizToplistProps) => {
	const { leaderboard, statusState, errorType } = useLeaderboard(config);
	const {
		state: { user: currentUser },
	} = useGlobalState();

	console.log('toplist user', currentUser);

	const renderHeader = () => {
		if (!showHeader) return null;

		return (
			<div className='biz-toplist-header'>
				<div className='biz-toplist-header-icon'>{statusState === 'LOADING' && <BizIcon icon='LOADING' />}</div>
				<div className='biz-toplist-header-title'>
					<span>{title}</span>
					<span className='biz-toplist-header-scope'>{getScopeLabel(config.scope, currentUser?.Units?.[0])}</span>
				</div>
			</div>
		);
	};

	const renderEntry = (entry: LeaderboardEntry) => {
		const isCurrentUser = currentUser && entry.UserId === currentUser.Id;
		const isTop3 = entry.Rank <= 3;
		const fullName = `${entry.UserFirstName} ${entry.UserLastName}`.trim();

		const itemClasses = [
			'biz-toplist-item',
			isCurrentUser ? 'biz-toplist-item--current-user' : '',
			isTop3 ? `biz-toplist-item--top-3 biz-toplist-item--rank-${entry.Rank}` : '',
		]
			.filter(Boolean)
			.join(' ');

		const rankClasses = ['biz-toplist-rank', isTop3 ? `biz-toplist-rank--${entry.Rank}` : ''].filter(Boolean).join(' ');

		return (
			<li key={entry.UserId} className={itemClasses}>
				<span className={rankClasses}>{entry.Rank}</span>
				<div className='biz-toplist-user-info'>
					<span className='biz-toplist-user-name' title={fullName}>
						{fullName}
					</span>
				</div>
				<div className='biz-toplist-xp'>
					<span>{formatXP(entry.DefaultExperiencePoints)}</span>
					<span className='biz-toplist-xp-label'>XP</span>
				</div>
			</li>
		);
	};

	const renderContent = () => {
		if (statusState === 'LOADING') {
			return <div className='biz-toplist-loading'>Loading...</div>;
		}

		if (statusState === 'ERROR') {
			return (
				<div className='biz-toplist-error'>
					{errorType === 'FORBIDDEN'
						? 'You do not have permission to view this leaderboard.'
						: 'Failed to load leaderboard. Please try again.'}
				</div>
			);
		}

		if (!leaderboard || leaderboard.length === 0) {
			return <div className='biz-toplist-empty'>No entries found.</div>;
		}

		return <ul className='biz-toplist-list'>{leaderboard.map(renderEntry)}</ul>;
	};

	const containerClasses = ['biz-toplist', legacy ? 'legacy' : '', className].filter(Boolean).join(' ');

	return (
		<div className={containerClasses}>
			{renderHeader()}
			{renderContent()}
		</div>
	);
};

export default BizToplist;
